/**
 * MediaPipeline.AudioNative — browser-native audio processing.
 *
 * Decode → process → encode without WebAssembly. Replaces the FFmpeg WASM
 * path for every operation that has a Web Audio / lamejs equivalent. Used
 * by tools whose operation set fits within Web Audio capabilities (trim /
 * volume / fade / EQ / crossfade / concat / fade_silence). Tools needing
 * loudnorm 2-pass / compound DSP / exotic codecs route to AudioWasm.
 *
 * Capabilities (operation manifest tokens):
 *   {kind: 'trim',    start: 0.5, end: 30.0}
 *   {kind: 'gain',    value: 1.5}                  // 1.0 = unity
 *   {kind: 'fadeIn',  duration: 2.0}
 *   {kind: 'fadeOut', duration: 2.0}
 *   {kind: 'eqBass',  gain: -3}                    // dB, lowshelf 100Hz
 *   {kind: 'eqTreble',gain: 4}                     // dB, highshelf 8kHz
 *   {kind: 'reverse'}
 *   {kind: 'speed',   factor: 1.25}                // playbackRate; pitch shifts
 *
 * Encode targets: 'mp3' (via lamejs / WebCodecs.AudioEncoder when available),
 * 'wav' (native PCM writer). Anything else → caller routes to AudioWasm.
 *
 * Memory guard. Estimates peak PCM bytes from input duration × channels × 4 ×
 * a 3× safety factor (rendered + WAV/Int16 copy + encode intermediate). If
 * the prediction exceeds 25% of `navigator.deviceMemory`, returns
 * {feasible: false, reason: 'memory'} so the router can fall back to
 * streaming WASM.
 */
(function(){
'use strict';

function isIOSWebKit(){
    // iOS Safari + Chrome iOS (CriOS) + Yandex iOS + Firefox iOS — all run on
    // WebKit under the hood (App Store policy). They share the same memory
    // budget envelope and the same lack of `navigator.deviceMemory`.
    var ua = (navigator.userAgent || '');
    return /iP(hone|ad|od)/.test(ua);
}

function deviceMemoryCapBytes(){
    var dm = navigator.deviceMemory;
    // On iOS WebKit deviceMemory is NOT exposed (Chromium-only API).
    // Pre-2026-06-13 the legacy `|| 4` default × 25% gave a 1 GB cap which
    // is far too restrictive for iOS Web Audio (To_li_man feedback
    // 2026-06-13: 26-min stereo input predicted 1.8 GB peak → forced
    // tree_merge route → FFmpeg WASM doesn't reliably init on iOS WebKit
    // (Instantiate timeout @50-60s) → hard wall, user gets nothing).
    // Empirically iOS Safari handles AudioBuffer allocations up to about
    // 2 GB before throwing on modern iPhones (4+ GB RAM, iOS 16+). Raising
    // the cap there gives the lite path a chance; if it OOMs the catch
    // surfaces an honest "too big for iOS" message, which is strictly
    // better than today's "FFmpeg failed to load" dead-end. On older
    // 2 GB iPhones the 2 GB lift may still kill the tab — but that path
    // failed before too, just less politely.
    if (dm === undefined || dm === 0) {
        if (isIOSWebKit()) return 2 * 1024 * 1024 * 1024;
        return 4 * 1024 * 1024 * 1024 * 0.25;
    }
    return dm * 1024 * 1024 * 1024 * 0.25;
}

// `sampleRate` is optional and defaults to 48000 so every existing caller keeps
// its current answer. Pass the real rate when known: a 96 kHz source costs
// twice the Float32 this assumed, and now that renders keep the source rate
// (see applyOps) that gap is real rather than theoretical.
function feasibleByMemory(durationSec, channels, sampleRate){
    var rate = (sampleRate > 0) ? sampleRate : 48000;
    var bytes = Math.ceil((durationSec || 0) * rate) * (channels || 2) * 4 * 3;
    var capBytes = deviceMemoryCapBytes();
    return {
        feasible: bytes <= capBytes,
        peakMB: Math.round(bytes / 1048576),
        capMB: Math.round(capBytes / 1048576)
    };
}

// Detect native channel count we should target. Caller can override via
// opts.channels; otherwise we take max across decoded buffers, capped at 2.
function targetChannels(buffers, override){
    if (override === 1 || override === 2) return override;
    var ch = 1;
    for (var i = 0; i < buffers.length; i++) {
        if (buffers[i] && buffers[i].numberOfChannels > ch) {
            ch = Math.min(buffers[i].numberOfChannels, 2);
        }
    }
    return ch;
}

// Decode a File / Blob via AudioContext. The browser uses a hardware-
// accelerated decoder for mp3/aac/m4a/ogg/opus/wav/flac (varies by browser).
// Delegates to window.AudioDecoderRobust.robust when available — that's the
// project-wide single source of truth for the three-tier recovery chain
// (native → FFmpeg WASM → AudioRecover) and emits the standardised
// telemetry. Falls back to a minimal native decode if the module
// somehow didn't load (defer race / partial deploy / unit-test harness).
async function decodeFile(file){
    if (window.AudioDecoderRobust && typeof window.AudioDecoderRobust.robust === 'function') {
        var r = await window.AudioDecoderRobust.robust(file, null, { slug: 'media-pipeline' });
        return r.buffer;
    }
    var ctx = new (window.AudioContext || window.webkitAudioContext)();
    try {
        var arr = await file.arrayBuffer();
        return await ctx.decodeAudioData(arr.slice ? arr.slice(0) : arr);
    } finally {
        try { ctx.close(); } catch(_){}
    }
}

async function decodeFiles(files){
    var bufs = [];
    for (var i = 0; i < files.length; i++) {
        bufs.push(await decodeFile(files[i]));
    }
    return bufs;
}

// Process a single AudioBuffer through a list of operations.
// Returns a new AudioBuffer with all operations applied in order.
async function applyOps(buffer, operations, outChannels, outRate){
    operations = operations || [];
    var srcSR = buffer.sampleRate;
    // Default: keep the source rate. The RATE policy (what the chosen encoder
    // can carry) is resolved by the caller — process() via encoderRateFor —
    // exactly like the channel policy below. Was a hard-coded 48000, which
    // upsampled every 44.1 kHz file (bigger file, no added quality) and
    // downsampled every 96 kHz one (measured on prod 2026-07-30: /trim 44.1 →
    // 48000, /volume 96k → 48000).
    var targetSR = outRate || srcSR || 48000;
    // Default: keep the source layout. The channel POLICY (how many channels
    // the chosen output format can actually carry) is decided by the caller —
    // process() resolves it via encoderMaxChannels() — so this primitive only
    // executes it. Was `Math.min(ch, 2)`, an unconditional cap that silently
    // folded every 5.1/7.1 source to stereo even for formats that carry
    // surround perfectly well (measured on prod 2026-07-29: 6ch in → 2ch out).
    var ch = outChannels || buffer.numberOfChannels;

    // Compute trim window first — affects target sample count.
    var trimStart = 0;
    var trimEnd = buffer.duration;
    var speedFactor = 1;
    var reverseFlag = false;
    var gain = 1;
    var fadeIn = 0;
    var fadeOut = 0;
    var eqBass = 0;
    var eqTreble = 0;
    var ceilingLimit = 0;
    var envelopePoints = null;

    for (var i = 0; i < operations.length; i++) {
        var op = operations[i];
        if (!op || !op.kind) continue;
        switch (op.kind) {
            case 'trim':
                trimStart = Math.max(0, op.start || 0);
                trimEnd = Math.min(buffer.duration, op.end != null ? op.end : buffer.duration);
                break;
            case 'gain':    gain = (op.value != null) ? op.value : 1; break;
            case 'fadeIn':  fadeIn = op.duration || 0; break;
            case 'fadeOut': fadeOut = op.duration || 0; break;
            // Огибающая — обобщение gain+fadeIn+fadeOut на произвольную кривую.
            // Точки строит `GainEnvelope`, единственное место, где живёт ответ
            // на вопрос «какое усиление в момент t»; здесь только исполнение.
            case 'envelope': envelopePoints = op.points || null; break;
            case 'eqBass':   eqBass = op.gain || 0; break;
            case 'eqTreble': eqTreble = op.gain || 0; break;
            case 'reverse': reverseFlag = true; break;
            case 'speed':   speedFactor = op.factor || 1; break;
            // Потолок выхода: положительный gain/EQ на громком источнике
            // уводит рендер выше 0 дБFS, и int16-энкодеры ниже жёстко его
            // срезают — класс «в браузере одно, в файле треск» (замер
            // 19.08.2026 на /volume: +6 дБ → 72% сэмплов в потолке).
            // Применяется ПОСЛЕ рендера; неперегруженный рендер не трогается.
            case 'ceiling': ceilingLimit = (op.limit != null) ? op.limit : 0.944; break;
        }
    }

    var trimmedDur = Math.max(0, trimEnd - trimStart);
    if (trimmedDur <= 0) throw new Error('zero-length trim window');

    // Output duration after speed change. speed>1 makes audio shorter,
    // speed<1 makes it longer. playbackRate-style, no preserve-pitch.
    var outDur = trimmedDur / (speedFactor || 1);
    var sampleCount = Math.ceil(outDur * targetSR);
    if (sampleCount <= 0) throw new Error('zero-length output');

    // Reverse must run BEFORE the gain-automated render: FFmpeg's
    // `areverse,afade=in` semantic places the fade-in at the start of
    // the REVERSED audio, not at the original start. If we reversed the
    // rendered buffer post-hoc, the fade window would land at the wrong
    // end of the timeline. So we materialise a reversed copy of the
    // source-trimmed window as the BufferSource input, then let trim/
    // fade/EQ/speed run as if it were the original.
    var srcBuffer = buffer;
    var srcTrimStart = trimStart;
    var srcTrimDur = trimmedDur;
    if (reverseFlag) {
        var inSR = buffer.sampleRate;
        var startSample = Math.floor(trimStart * inSR);
        var lenSample = Math.floor(trimmedDur * inSR);
        // Route through window.__safeAudioBuffer (timbrica-core.js) for the
        // Android-Chrome length-cap telemetry coverage. Falls back to a
        // temporary AudioContext.createBuffer if the helper isn't loaded.
        var allocCtx = new (window.AudioContext || window.webkitAudioContext)();
        var revBuf;
        try {
            revBuf = (typeof window.__safeAudioBuffer === 'function')
                ? window.__safeAudioBuffer({numberOfChannels: buffer.numberOfChannels, length: lenSample, sampleRate: inSR}, allocCtx)
                : allocCtx.createBuffer(buffer.numberOfChannels, lenSample, inSR);
            for (var rc = 0; rc < buffer.numberOfChannels; rc++) {
                var srcChan = buffer.getChannelData(rc);
                var dstChan = revBuf.getChannelData(rc);
                for (var i = 0, j = lenSample - 1; i < lenSample; i++, j--) {
                    dstChan[i] = srcChan[startSample + j];
                }
            }
        } finally {
            try { allocCtx.close(); } catch(_){}
        }
        srcBuffer = revBuf;
        srcTrimStart = 0;          // already cropped during the reverse copy
        srcTrimDur = trimmedDur;
    }

    var offline = new OfflineAudioContext(ch, sampleCount, targetSR);
    var src = offline.createBufferSource();
    src.buffer = srcBuffer;
    src.playbackRate.value = speedFactor;

    var node = src;

    // Gain + fade automation. Fade duration is interpreted in OUTPUT
    // time (matching FFmpeg's `afade=d=N` which is N output seconds
    // regardless of any preceding atempo). Don't divide by speedFactor.
    var g = offline.createGain();
    if (envelopePoints && envelopePoints.length >= 2) {
        var GE = (typeof window !== 'undefined') ? window.GainEnvelope : null;
        if (!GE) throw new Error('envelope op requires gain-envelope.js');
        // Времена огибающей — в ИСХОДНОМ времени файла, а рендер начинается с
        // trimStart. Растяжение и реверс сместили бы это соответствие, и
        // огибающая молча легла бы не туда: лучше отказать и уйти на другой
        // лейн, чем отдать правдоподобно неверный файл.
        if (speedFactor !== 1 || reverseFlag) throw new Error('envelope op is not defined together with speed/reverse');
        // ТОТ ЖЕ вызов, что делает превью на живом AudioContext. Web Audio
        // исполняет контракт сэмплово-точно в аудиопотоке — именно поэтому
        // превью и файл больше не могут разойтись.
        GE.fromPoints(envelopePoints).scheduleOn(g.gain, 0, trimStart);
    } else {
        g.gain.setValueAtTime(gain, 0);
        if (fadeIn > 0) {
            var fIn = Math.min(fadeIn, outDur);
            g.gain.setValueAtTime(0, 0);
            g.gain.linearRampToValueAtTime(gain, fIn);
        }
        if (fadeOut > 0) {
            var fOut = Math.min(fadeOut, outDur);
            g.gain.setValueAtTime(gain, outDur - fOut);
            g.gain.linearRampToValueAtTime(0, outDur);
        }
    }
    node.connect(g);
    node = g;

    if (eqBass !== 0) {
        var bf = offline.createBiquadFilter();
        bf.type = 'lowshelf'; bf.frequency.value = 100; bf.gain.value = eqBass;
        node.connect(bf); node = bf;
    }
    if (eqTreble !== 0) {
        var tf = offline.createBiquadFilter();
        tf.type = 'highshelf'; tf.frequency.value = 8000; tf.gain.value = eqTreble;
        node.connect(tf); node = tf;
    }

    node.connect(offline.destination);
    try { src.start(0, srcTrimStart, srcTrimDur); } catch(_){}

    var rendered = await offline.startRendering();
    if (ceilingLimit > 0) rendered = applyCeiling(rendered, ceilingLimit);
    return rendered;
}

/* Потолок готового рендера. peak ≤ limit → буфер не тронут (байт-в-байт).
   С MasteringLimiter на странице — true-peak лимитер (давит только окна
   перегруза, ниже потолка бит-прозрачен; выбран замером против статического
   спуска — тот топит весь файл за перегруз одного места). Без модуля —
   статический спуск: тише, но честнее среза. */
function applyCeiling(rendered, limit) {
    var peak = 0, c, d, i, v;
    for (c = 0; c < rendered.numberOfChannels; c++) {
        d = rendered.getChannelData(c);
        for (i = 0; i < d.length; i++) { v = d[i] < 0 ? -d[i] : d[i]; if (v > peak) peak = v; }
    }
    if (peak <= limit) return rendered;
    var ML = (typeof window !== 'undefined') ? window.MasteringLimiter : null;
    if (ML && ML.limit) {
        var chans = [];
        for (c = 0; c < rendered.numberOfChannels; c++) chans.push(rendered.getChannelData(c));
        var r = ML.limit(chans, rendered.sampleRate, { ceilingDb: 20 * Math.log10(limit) });
        for (c = 0; c < rendered.numberOfChannels; c++) rendered.getChannelData(c).set(r.out[c]);
    } else {
        var g = limit / peak;
        for (c = 0; c < rendered.numberOfChannels; c++) {
            d = rendered.getChannelData(c);
            for (i = 0; i < d.length; i++) d[i] *= g;
        }
    }
    return rendered;
}

/* __pcm16:begin
 * float → 16 бит. ОДИН квантователь сайта: этот блок одинаков (с точностью
 * до отступа) в audio-pipeline.js, media-pipeline/audio-native.js,
 * audio-post.js, audio-output-sink.js, audio-recover.js и timbrica-core.js
 * (там — window.__pcm16Quantizer для тулов со своим Int16; сторож —
 * tests/js/pcm16-quantize.test.mjs). Замеры и отвергнутые варианты —
 * docs/why/pcm16-quantize.md.
 *
 * pcm16Quantizer(dither) → q(src, n, dst, at, stride): n выборок канала src
 * пишутся в dst[at], dst[at + stride], … (Int16Array, каналы чередуются).
 *  · Масштаб — как у декодеров Chrome и Firefox (минус ×32768, плюс ×32767):
 *    нетронутый 16-битный звук возвращается бит-в-бит. Прежнее отрезание
 *    `| 0` меняло 27–30 % его выборок на единицу к нулю.
 *  · Блок из 1024 выборок, целиком лежащий на 16-битной сетке (нетронутый
 *    звук, цифровая тишина, выход декодеров с делением на 32768), копируется
 *    точно — без шума и без округления мимо.
 *  · dither=true — TPDF ±1 младший бит (готовый файл: звук тише младшего бита
 *    уходит в шум, а не в тишину и не в гармоники).
 *  · dither=false — округление «половина к чётному», без шума: для того, что
 *    ещё будут складывать (стемы) — сумма четырёх стемов на 9,6 дБ тише по шуму.
 *  · Зерно постоянное: один и тот же вход — одни и те же байты.
 */
function pcm16Quantizer(dither) {
    var x = 0x9E3779B9, TOL = 1 / 256, BLOCK = 1024;
    // Весь блок лежит на сетке ОДНОГО декодера (плюс ×pos: 32767 — Chrome и
    // Firefox, 32768 — ffmpeg)? Тогда это нетронутый звук: пишем точно.
    // Сетка выбирается на блок: по выборке выход ffmpeg у полной шкалы
    // (32640…32767) ложно садился на сетку 32767 и терял единицу.
    function exact(src, b, e, dst, at, stride, pos) {
        for (var i = b, w = at + b * stride; i < e; i++, w += stride) {
            var s = src[i];
            if (s !== s) s = 0;
            var v = s < 0 ? s * 32768 : s * pos, k = Math.floor(v + 0.5), d = v - k;
            if (d > TOL || d < -TOL) return false;
            dst[w] = k > 32767 ? 32767 : (k < -32768 ? -32768 : k);
        }
        return true;
    }
    return function (src, n, dst, at, stride) {
        // Состояние генератора — в локальной на время вызова: через замыкание
        // горячий цикл вдвое-вчетверо медленнее (замер в Chrome 156).
        var b, e, i, w, s, v, k, r = x, tri = dither;
        for (b = 0; b < n; b += BLOCK) {
            e = b + BLOCK < n ? b + BLOCK : n;
            if (exact(src, b, e, dst, at, stride, 32767) || exact(src, b, e, dst, at, stride, 32768)) continue;
            for (i = b, w = at + b * stride; i < e; i++, w += stride) {
                s = src[i];
                if (s !== s) s = 0;
                v = s < 0 ? s * 32768 : s * 32767;
                if (tri) {
                    r ^= r << 13; r ^= r >>> 17; r ^= r << 5;
                    k = Math.floor(v + ((r & 0xFFFF) + ((r >>> 16) & 0xFFFF)) * 1.52587890625e-5 - 0.5);  // ×2^-16
                } else {
                    k = Math.floor(v + 0.5);
                    if (k - v === 0.5 && (k & 1)) k--;
                }
                dst[w] = k > 32767 ? 32767 : (k < -32768 ? -32768 : k);
            }
        }
        x = r;
    };
}
/* __pcm16:end */

// PCM Float32 (per-channel) → 16-bit interleaved WAV blob. Same layout as
// AudioPipeline.audioBufferToWav, duplicated here so AudioNative is usable
// without the full AudioPipeline loader (e.g. SW context). Quantization is the
// shared pcm16Quantizer block above, WITH dither: a lite-lane WAV is the
// person's finished file, not a stem (AudioPipeline's default is the rounded
// mode for that reason).
//
// This copy had DRIFTED from that original and was wrong for any channel
// count other than 2: it interleaved only the stereo case and otherwise wrote
// channel 0 alone — while still stamping the real count into the header. A
// 5.1 buffer therefore produced a file DECLARING 6 channels with one
// channel's worth of samples in it: a player reads the header, spreads that
// single channel across six, and gets garbled audio at 1/6 the duration.
// Measured 2026-07-29 on prod (6ch in → header 6, 96 KB instead of 576 KB).
// That was the root of the "lite path garbles >2ch" behaviour individual
// tools had been working around one by one.
function audioBufferToWav(buffer){
    var numCh = buffer.numberOfChannels, sr = buffer.sampleRate, bps = 16, ba = numCh * (bps / 8);
    // Interleave EVERY channel: WAV frames are L R (C LFE BL BR …) regardless
    // of count. Int16Array stores (not per-sample DataView.setInt16) keep the
    // serialization JIT-vectorized — the same optimization the AudioPipeline
    // original carries; WAV is little-endian like every platform we run on.
    var frames = buffer.length;
    var pcm = new Int16Array(frames * numCh);
    // Готовый файл — с дизерингом; нетронутый звук проходит бит-в-бит.
    var q = pcm16Quantizer(true);
    for (var ch = 0; ch < numCh; ch++) q(buffer.getChannelData(ch), frames, pcm, ch, numCh);
    var dl = pcm.length * (bps / 8);
    var head = new ArrayBuffer(44), v = new DataView(head);
    function ws(o, s){ for (var k = 0; k < s.length; k++) v.setUint8(o + k, s.charCodeAt(k)); }
    ws(0, 'RIFF'); v.setUint32(4, 36 + dl, true); ws(8, 'WAVE'); ws(12, 'fmt ');
    v.setUint32(16, 16, true); v.setUint16(20, 1, true); v.setUint16(22, numCh, true);
    v.setUint32(24, sr, true); v.setUint32(28, sr * ba, true); v.setUint16(32, ba, true);
    v.setUint16(34, bps, true); ws(36, 'data'); v.setUint32(40, dl, true);
    return new Blob([head, pcm.buffer], { type: 'audio/wav' });
}

// Capability matrix for WebCodecs.AudioEncoder. Each entry is a codec
// identifier the encoder accepts. Browser support varies by codec —
// Chrome 116+ has mp3, Chrome 124+ has flac, Safari 17+ has aac, etc.
// isConfigSupported() is the only honest way to check.
var WEBCODECS_CONFIGS = {
    flac:  { codec: 'flac' },
    mp3:   { codec: 'mp3' },
    opus:  { codec: 'opus' },
    aac:   { codec: 'mp4a.40.2' },  // AAC-LC
};

// Cache results of isConfigSupported per codec — these are stable
// per session and the API call itself isn't free.
var _wcSupport = {};
async function webCodecsSupports(fmt, sampleRate, channels, bitrate){
    if (typeof AudioEncoder === 'undefined') return false;
    if (!WEBCODECS_CONFIGS[fmt]) return false;
    var cfg = Object.assign({}, WEBCODECS_CONFIGS[fmt], {
        sampleRate: sampleRate || 48000,
        numberOfChannels: channels || 2,
        bitrate: bitrate || 192000,
    });
    var key = fmt + ':' + cfg.sampleRate + ':' + cfg.numberOfChannels + ':' + cfg.bitrate;
    if (_wcSupport[key] !== undefined) return _wcSupport[key];
    try {
        var res = await AudioEncoder.isConfigSupported(cfg);
        _wcSupport[key] = !!(res && res.supported);
    } catch (_) {
        _wcSupport[key] = false;
    }
    return _wcSupport[key];
}

// Encode an AudioBuffer using WebCodecs.AudioEncoder. Returns raw codec
// frames concatenated as a Blob. NOTE: this is NOT a fully-muxed file
// for AAC/OPUS — those need MP4/Ogg container wrapping which we don't
// implement yet. FLAC frames CAN be concatenated standalone (the .flac
// container is a thin sequence of frames with a leading header).
async function encodeViaWebCodecs(buffer, fmt, opts){
    opts = opts || {};
    var bitrate = (opts.bitrate || 192) * 1000;
    var sampleRate = buffer.sampleRate;
    var numChannels = buffer.numberOfChannels;

    var config = Object.assign({}, WEBCODECS_CONFIGS[fmt], {
        sampleRate: sampleRate,
        numberOfChannels: numChannels,
        bitrate: bitrate,
    });
    if (fmt === 'flac') {
        // FLAC accepts only 16-bit PCM input via WebCodecs. Specify it
        // explicitly so the encoder doesn't default to f32.
        config.flac = { blockSize: 4096, compressLevel: 5 };
    }

    var chunks = [];
    var encoderError = null;
    var encoder = new AudioEncoder({
        output: function(chunk){
            var data = new Uint8Array(chunk.byteLength);
            chunk.copyTo(data);
            chunks.push(data);
        },
        error: function(e){ encoderError = e; },
    });
    encoder.configure(config);

    // Feed the buffer in 10-second chunks to keep memory bounded.
    var chunkSec = 10;
    var chunkSamples = chunkSec * sampleRate;
    var totalSamples = buffer.length;
    var interleaved = new Float32Array(chunkSamples * numChannels);

    for (var off = 0; off < totalSamples; off += chunkSamples) {
        if (encoderError) throw encoderError;
        var len = Math.min(chunkSamples, totalSamples - off);
        // Interleave channels for AudioData
        for (var ch = 0; ch < numChannels; ch++) {
            var src = buffer.getChannelData(ch);
            for (var i = 0; i < len; i++) {
                interleaved[i * numChannels + ch] = src[off + i];
            }
        }
        var audioData = new AudioData({
            format: 'f32',
            sampleRate: sampleRate,
            numberOfChannels: numChannels,
            numberOfFrames: len,
            timestamp: Math.round((off / sampleRate) * 1e6),  // microseconds
            data: interleaved.slice(0, len * numChannels),
        });
        encoder.encode(audioData);
        audioData.close();
        // Heartbeat for caller-side stall guards. WebCodecs feeds in 10s
        // chunks; emit progress per chunk so a watching __withStallGuard
        // sees liveness even on a 30-min file.
        if (typeof opts.onProgress === 'function') {
            try { opts.onProgress(Math.min(1, (off + len) / totalSamples)); } catch(_){}
        }
    }

    await encoder.flush();
    encoder.close();
    if (encoderError) throw encoderError;

    var totalLen = chunks.reduce(function(s, c){ return s + c.length; }, 0);
    var out = new Uint8Array(totalLen);
    var pos = 0;
    for (var k = 0; k < chunks.length; k++) {
        out.set(chunks[k], pos);
        pos += chunks[k].length;
    }

    var mime = fmt === 'flac' ? 'audio/flac'
             : fmt === 'mp3'  ? 'audio/mpeg'
             : fmt === 'opus' ? 'audio/opus'
             : fmt === 'aac'  ? 'audio/aac'
             : 'application/octet-stream';
    return new Blob([out], { type: mime });
}

/**
 * How many channels OUR encoder for `fmt` can actually deliver, given a source
 * with `srcCh` channels. This is deliberately about our own capability, not
 * about what the container spec allows in theory — the per-format ceiling
 * users see in the UI stays ChannelModule's job (config/formats.php).
 *
 * Never returns more than the source has: this resolves a downmix ceiling,
 * never a fabricated upmix (that is UpmixModule's matrix, opt-in per tool).
 *
 * Rendering to the returned count means an over-capacity source is folded by
 * the OfflineAudioContext itself — i.e. a spec-correct downmix that keeps the
 * centre and rears mixed in, NOT a "drop channels 3..6" truncation.
 *
 * Measured 2026-07-29 (Chrome, prod): AAC accepts 6 channels, OPUS and FLAC
 * only 2 — hence asking the browser per format instead of hardcoding a table
 * that would go stale as codecs ship.
 */
async function encoderMaxChannels(fmt, sampleRate, srcCh, bitrate){
    var n = Math.max(1, srcCh || 1);
    if (n <= 2) return n;                       // nothing to decide
    if (fmt === 'wav') return n;                // our PCM writer handles any N
    if (fmt === 'mp3') return 2;                // MPEG-1 Layer III tops out at 2
    if (WEBCODECS_CONFIGS[fmt]) {
        // Walk down from the source count to the widest layout this browser
        // will actually encode. Results are cached inside webCodecsSupports.
        for (var c = n; c > 2; c--) {
            if (await webCodecsSupports(fmt, sampleRate, c, bitrate)) return c;
        }
    }
    return 2;
}

/**
 * Which sample rate OUR encoder for `fmt` can actually deliver, given a source
 * at `srcRate`. Sibling of encoderMaxChannels — same rule: never invent what
 * the source doesn't have (no upsampling), only clamp down when the encoder
 * genuinely cannot carry the rate.
 *
 * MPEG-1 Layer III is the only real ceiling here: 32 / 44.1 / 48 kHz (MPEG-2
 * adds 16 / 22.05 / 24). WAV and FLAC carry anything we can render, and the
 * WebCodecs formats are probed per configuration at encode time.
 */
// Per-format rate whitelists come from config/formats.php via
// window.__FFMPEG_CONFIG__.sampleRates (same delivery as maxChannels). The
// fallback list is only for pages rendered without that config; keeping a
// second hand-written table was how a 48 kHz master ended up at 44.1 kHz on
// some paths and not others.
var MP3_RATES_FALLBACK = [48000, 44100, 32000, 24000, 22050, 16000];
function ratesFor(fmt){
    var acl = window.__audioCodecLimits;
    if (acl && typeof acl.ratesFor === 'function') {
        var r = acl.ratesFor(fmt);
        if (r) return r;
        if (window.__FFMPEG_CONFIG__ && window.__FFMPEG_CONFIG__.sampleRates) return null;
    }
    var cfg = window.__FFMPEG_CONFIG__ && window.__FFMPEG_CONFIG__.sampleRates;
    if (cfg && cfg[fmt] && cfg[fmt].length) return cfg[fmt];
    if (cfg) return null;                      // config present, format unlimited
    return (fmt === 'mp3') ? MP3_RATES_FALLBACK : null;
}
/* Правило «ближайшая поддерживаемая не выше запрошенной» теперь одно на всех —
 * window.__audioCodecLimits.rateFor (timbrica-core.js). Здесь остаётся вызов и
 * прежний запасной путь: этот модуль грузится и на страницах, куда ядро могло
 * не доехать, а вернуть srcRate для Opus значило бы сконфигурировать энкодер
 * частотой, которой у Opus не бывает. */
function encoderRateFor(fmt, srcRate){
    if (!srcRate || srcRate <= 0) return 48000;
    var acl = window.__audioCodecLimits;
    if (acl && typeof acl.rateFor === 'function' && acl.ratesFor(fmt)) {
        return acl.rateFor(fmt, srcRate);
    }
    var rates = ratesFor(fmt);
    if (!rates) return srcRate;                // format carries any rate
    if (rates.indexOf(srcRate) !== -1) return srcRate;
    for (var i = 0; i < rates.length; i++) if (rates[i] < srcRate) return rates[i];
    return rates[rates.length - 1];            // below all of them: closest is the lowest
}

// Encode a rendered AudioBuffer into the requested format.
//   wav  → native PCM writer (always available)
//   mp3  → AudioPipeline.encodeMP3 (lamejs primary, FFmpeg fallback)
//   flac → WebCodecs.AudioEncoder when supported (Chrome 124+, Safari 17.4+)
//   aac/opus → WebCodecs (raw frames; NOT muxed yet — caller can use)
async function encode(buffer, fmt, opts){
    opts = opts || {};
    if (fmt === 'wav') {
        return audioBufferToWav(buffer);
    }
    // WebCodecs path for FLAC/AAC/OPUS — pure-JS encoders for these
    // formats either don't exist or are 1-2 MB heavy. Try WebCodecs
    // first if browser supports the codec; otherwise throw and let
    // caller fall through to FFmpeg WASM.
    if (fmt === 'flac' || fmt === 'aac' || fmt === 'opus') {
        // Ask about the layout we are ACTUALLY going to encode. Was
        // `Math.min(ch, 2)`: the probe asked "can you do stereo?" and then
        // encodeViaWebCodecs configured the encoder with the real channel
        // count — so a 6-channel buffer was handed to an encoder validated
        // only for 2.
        var ok = await webCodecsSupports(fmt, buffer.sampleRate, buffer.numberOfChannels, opts.bitrate);
        if (ok) {
            return await encodeViaWebCodecs(buffer, fmt, opts);
        }
        throw new Error('AudioNative cannot encode ' + fmt + ' (WebCodecs unavailable on this browser)');
    }
    if (fmt === 'mp3') {
        if (!window.AudioPipeline || !window.AudioPipeline.encodeMP3) {
            throw new Error('AudioPipeline not loaded for mp3 encode');
        }
        return await window.AudioPipeline.encodeMP3(buffer, {
            bitrate: opts.bitrate || 192,
            slug: opts.slug,
            onProgress: opts.onProgress  // surface lamejs Worker heartbeat to caller
        });
    }
    throw new Error('AudioNative cannot encode ' + fmt);
}

/**
 * Top-level entry: decode → applyOps → encode.
 *
 *   await MediaPipeline.audio.process({
 *       input: file,                       // File or Blob
 *       operations: [{kind:'trim',start:0,end:30}, {kind:'gain',value:1.5}],
 *       output: { format: 'mp3', bitrate: 192 },
 *       slug: 'volume',
 *       onProgress: function(pct){}
 *   })
 */
// Lossless trim short-circuit (2026-05-03, bug-report kocevnik05).
// For the most common operation in the catalogue — single-segment trim with
// no fades, no gain, no cuts, input format == output format — we skip the
// decode→render→encode pipeline entirely. MP3 is frame-based, WAV is raw
// PCM with a header; both can be cut at byte level without allocating a
// 200+ MB AudioBuffer that low-RAM devices can't hold. This is the cure
// for the "stuck at 65%" class of bugs (lamejs Worker zombiefied by
// Android low-memory killer mid-encode of full-PCM buffer).
//
// Falls through to the full pipeline if:
//   - operations contain anything other than a single 'trim'
//   - input format != output format (need re-encode)
//   - format isn't mp3 or wav
//   - the lossless trimmer module isn't loaded (defer race)
//   - the lossless trimmer throws (corrupt file / unknown structure)
//   - magic-byte sniff disagrees with the declared MIME/extension
//
// Why the magic-byte sniff exists (2026-05-07, prod telemetry):
// 13 of 21 lossless_trim_fallthrough events in the first 3 days were AAC-
// in-MP4 files (e.g. .m4a renamed/served as audio/mpeg by some browsers
// for files like Recording_NNN.m4a). MIME and extension were both lying.
// The MP3 parser correctly rejected (no MP3 sync bytes) and the user got
// a result via the decode/encode fallback, but it was wasted CPU + dirty
// telemetry. Reading 16 bytes up front is ~zero cost and routes such
// files away from the lossless path before we touch the parser.
async function detectActualFormat(blob, slug){
    if (!blob || typeof blob.slice !== 'function') return null;
    try {
        var head = new Uint8Array(await blob.slice(0, 16).arrayBuffer());
        if (head.length < 4) return null;
        // An ID3v2 tag or an MPEG sync word only NOMINATES mp3. ID3 fronts AAC
        // and FLAC just as well, and 0xFF + three set bits is also the ADTS
        // sync word — every raw .aac passed here as MP3, and a cut from the
        // start came out as the whole AAC file behind an MP3 header (measured
        // 2026-09-23: 9 of 24 such files). The stream verdict decides.
        if ((head[0] === 0x49 && head[1] === 0x44 && head[2] === 0x33)
            || (head[0] === 0xFF && (head[1] & 0xE0) === 0xE0)) {
            var T = window.Mp3FrameTrim;
            return (T && typeof T.sniffMpeg === 'function' && await T.sniffMpeg(blob, { slug: slug }))
                ? 'mp3' : null;
        }
        // RIFF...WAVE
        if (head.length >= 12
            && head[0] === 0x52 && head[1] === 0x49 && head[2] === 0x46 && head[3] === 0x46
            && head[8] === 0x57 && head[9] === 0x41 && head[10] === 0x56 && head[11] === 0x45) return 'wav';
        return null;
    } catch (e) {
        return null;
    }
}

/* What the fade lane did, for `pipeline_route`: how the ramps were made
   (fm), the bitrate our frames were encoded at (fkb), how the seam after
   the fade-in was sealed (seal: none / fit / grown / spread / lower, and at
   which bitrate), how far the seams moved from the ramps (ext, frames), and what
   the re-decode of each seam measured (dB against the source; null — the
   whole region was ours and there was no seam). */
function fadeTelemetry(r){
    if (!r || !r.method) return null;
    var fi = r.fadeIn, fo = r.fadeOut, db = function(v){ return typeof v === 'number' ? Math.round(v) : null; };
    var ci = fi && fi.check, co = fo && fo.check;
    return {
        fm: r.method, shift: typeof r.shift === 'number' ? r.shift : null,
        fkb: (fi && fi.kbps) || (fo && fo.kbps) || null,
        seal: fi ? fi.seal : null, sealKb: fi ? fi.sealKbps : null,
        extIn: fi ? fi.extFrames : null, extOut: fo ? fo.extFrames : null,
        seamIn: ci ? db(ci.seamDb) : null, seamOut: co ? db(co.seamDb) : null
    };
}

async function losslessTrimEligible(opts){
    var ops = opts.operations || [];
    if (ops.length === 0) return null;

    // Accept either:
    //   [trim]                         → pure lossless byte-slice
    //   [trim, fadeIn?, fadeOut?]      → hybrid: the frames under the ramps are
    //                                    decoded, faded and encoded again, the
    //                                    rest of the file is its own bytes
    // 2026-05-14 prod-observed gap (session bc6f8846, Android 9 / 2 GB /
    // 11 MB mp3 / trim+fade): simple trim+fade fell into lite path (full
    // decode+encode), lamejs Worker hung 30 s, watchdog fired and FFmpeg
    // fallback rescued the job — but the hybrid path SHOULD have caught it
    // first with O(fade) memory. The hybrid wire in trim.blade.php gated on
    // `cutRegions.length > 0`; this routing here makes the hybrid eligible
    // for the simple-trim+fade case too, via the regular pipeline entry.
    if (ops[0].kind !== 'trim') return null;
    var fadeInSec = 0;
    var fadeOutSec = 0;
    for (var i = 1; i < ops.length; i++) {
        var op = ops[i];
        if (op.kind === 'fadeIn') fadeInSec = Number(op.duration) || 0;
        else if (op.kind === 'fadeOut') fadeOutSec = Number(op.duration) || 0;
        else return null; // unknown op (gain, eqBass, etc.) — full pipeline
    }
    var hasFade = fadeInSec > 0 || fadeOutSec > 0;

    var fmt = (opts.output && opts.output.format) || 'mp3';
    if (fmt !== 'mp3' && fmt !== 'wav') return null;
    // WAV hybrid (with fade) is not implemented — fade requires decode which
    // bypasses the wav-frame-trim byte-slice path.
    if (hasFade && fmt !== 'mp3') return null;

    var input = opts.input;
    if (!input || typeof input.slice !== 'function') return null;
    var actualFmt = await detectActualFormat(input, opts.slug);
    if (actualFmt !== fmt) return null;
    if (fmt === 'mp3' && !(window.Mp3FrameTrim && window.Mp3FrameTrim.trim)) return null;
    if (fmt === 'wav' && !(window.WavFrameTrim && window.WavFrameTrim.trim)) return null;
    // A fade needs the page's MP3 encoder for the frames under the ramp. When
    // it is missing, or the result cannot be verified, multiTrimWithFades
    // throws and process() falls through to the full decode/encode pipeline
    // (lossless_trim_fallthrough) — the fade is then made on the samples of
    // the whole file.
    if (hasFade && !window.Mp3FrameTrim.multiTrimWithFades) return null;

    return {
        fmt: fmt,
        start: ops[0].start,
        end: ops[0].end,
        fadeInSec: fadeInSec,
        fadeOutSec: fadeOutSec,
        hasFade: hasFade,
    };
}

async function process(opts){
    if (!opts || !opts.input) throw new Error('process: input required');
    var fmt = (opts.output && opts.output.format) || 'mp3';
    var slug = opts.slug || 'media-pipeline';
    var prog = typeof opts.onProgress === 'function' ? opts.onProgress : function(){};

    // `lossless: false` — the caller has already tried the byte-level copy (a
    // planner ran AudioCut first) and wants the samples encoded; a second
    // attempt at the same copy would only cost time.
    var lossless = opts.lossless === false ? null : await losslessTrimEligible(opts);
    if (lossless) {
        try {
            prog(0.10);
            var r, fade = null;
            var routeName = 'lossless';
            if (lossless.hasFade) {
                // Hybrid: lossless middle + decoded fade boundaries.
                r = await window.Mp3FrameTrim.multiTrimWithFades(
                    opts.input,
                    [{ start: lossless.start, end: lossless.end }],
                    {
                        fadeInSec: lossless.fadeInSec,
                        fadeOutSec: lossless.fadeOutSec,
                        slug: slug,
                    }
                );
                // Hybrid result shape: {blob, regionsCopied, fadeIn, fadeOut}.
                // Synthesize the same return shape as lossless trim for caller.
                fade = fadeTelemetry(r);
                r = {
                    blob: r.blob,
                    actualStart: lossless.start,
                    actualEnd: lossless.end,
                };
                routeName = 'lossless_hybrid';
            } else if (lossless.fmt === 'mp3') {
                r = await window.Mp3FrameTrim.trim(opts.input, lossless.start, lossless.end);
            } else {
                r = await window.WavFrameTrim.trim(opts.input, lossless.start, lossless.end);
            }
            prog(1.0);
            try {
                if (window.__ae) window.__ae('feature_use', Object.assign({
                    slug: slug, f: 'pipeline_route', pipeline: routeName,
                    fmt: lossless.fmt,
                    fadeIn: !!lossless.fadeInSec, fadeOut: !!lossless.fadeOutSec
                }, fade || {}));
            } catch(_){}
            return {
                blob: r.blob,
                durationSec: r.actualEnd - r.actualStart,
                channels: r.channels || null,
                lossless: true
            };
        } catch (le) {
            // Trimmer failed (corrupt / unsupported structure). Fall through to
            // the regular decode→render→encode path so the user still gets a
            // result. Telemetry tells us which files trip this path so we can
            // harden the parsers.
            try {
                if (window.__ae) window.__ae('feature_use', {
                    slug: slug, f: 'lossless_trim_fallthrough',
                    fmt: lossless.fmt,
                    fade: !!lossless.hasFade,
                    code: (le && le.code) || null,
                    err: ((le && le.message) || '').substring(0, 120)
                });
            } catch(_){}
        }
    }

    prog(0.05);
    // opts.preDecoded: editor tools (trim / ringtone / silence-remover / volume)
    // already hold the AudioBuffer they presented in the waveform — paying the
    // decode cost a second time was pure waste AND doubled peak memory while
    // the duplicate buffer overlapped the encode. Accept the existing buffer
    // when the caller has it.
    var buffer = opts.preDecoded || await decodeFile(opts.input);
    prog(0.30);

    // Runtime memory feasibility check on the ACTUAL source duration. Callers
    // sometimes pass the output region to feasibleByMemory upstream (a known
    // historical bug in trim/ringtone/silence-remover — process() decodes the
    // FULL source first, so the relevant peak is source-shape, not output).
    // Throw a typed PipelineUnavailable here so the caller's catch routes to
    // the FFmpeg fallback intentionally rather than via a RangeError surprise.
    // Estimate against the REAL layout — a 5.1 source holds 3× the PCM of the
    // stereo the old `min(ch, 2)` assumed, so the cap was under-counting by
    // that factor and letting genuinely too-large surround files through.
    var srcFeas = feasibleByMemory(buffer.duration, buffer.numberOfChannels || 1, buffer.sampleRate);
    if (!srcFeas.feasible) {
        var pu = new Error('PipelineUnavailable: source ' + Math.round(buffer.duration) + 's exceeds memory cap (' + srcFeas.peakMB + 'MB > ' + srcFeas.capMB + 'MB)');
        pu.name = 'PipelineUnavailable';
        pu.peakMB = srcFeas.peakMB;
        pu.capMB = srcFeas.capMB;
        throw pu;
    }

    // Resolve the channel policy ONCE, here: an explicit caller request wins,
    // otherwise keep the source layout clamped to what our encoder for this
    // format can carry. Rendering at that count makes the OfflineAudioContext
    // perform any needed downmix (spec-correct, centre and rears folded in)
    // and lets a surround-capable format keep its channels end to end.
    var wantCh = opts.channels || await encoderMaxChannels(
        fmt, buffer.sampleRate, buffer.numberOfChannels, opts.output && opts.output.bitrate);
    var wantRate = opts.sampleRate || encoderRateFor(fmt, buffer.sampleRate);
    var processed = await applyOps(buffer, opts.operations || [], wantCh, wantRate);
    prog(0.65);

    var blob = await encode(processed, fmt, {
        bitrate: opts.output && opts.output.bitrate,
        slug: slug
    });
    prog(1.0);

    return { blob: blob, durationSec: processed.duration, channels: processed.numberOfChannels };
}

window.MediaPipelineAudioNative = {
    process: process,
    decodeFile: decodeFile,
    decodeFiles: decodeFiles,
    applyOps: applyOps,
    audioBufferToWav: audioBufferToWav,
    encode: encode,
    feasibleByMemory: feasibleByMemory,
    targetChannels: targetChannels
};

})();
